import React from 'react';

import { Collapse, IconButton, Typography } from '@mui/material';
import clsx from 'clsx';
import { StorableIcon } from 'src/components';
import { TrashcanIcon } from 'src/components/icons/TrashcanIcon';
import type { useGetCurrentDoc, useGetWorkflowFiles } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { HeadingsList } from './HeadingsList';
import type { OutlineMenuViewProps } from './types';

const useStyles = makeStyles()(() => ({
  deleteBtn: {
    '.document-header:hover > &': { display: 'block' },
    display: 'none',
    marginRight: '4px',
    paddingTop: '4px',
  },
  documentName: {
    color: '#324A52',
    fontSize: '12px',
    fontWeight: 600,
    marginRight: 'auto',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  header: {
    alignItems: 'center',
    borderRadius: '4px',
    display: 'flex',
    height: '26px',
  },
  headerBtn: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    border: 'none',
    display: 'flex',
    flexGrow: 1,
    gap: '5px',
    padding: '0 4px',
    // Ensure that truncation works correctly
    width: 0,
  },
  // hover and active have the same specificity.
  // we need to disable sorting to make sure active style is after hover
  // eslint-disable-next-line sort-keys
  interactive: {
    '& button': { cursor: 'pointer' },
    '&:hover': { backgroundColor: '#ebebeb' },
    '&:active': { backgroundColor: '#e0e0e0' },
  },
  isOpen: {
    position: 'sticky',
    top: '0',
  },
  loaded: {
    backgroundColor: '#d2e1f3',
  },
  loading: {
    backgroundColor: '#e0e0e0',
  },
}));

type DocumentType =
  | ReturnType<typeof useGetWorkflowFiles>['workflowFiles'][number]['document']
  | ReturnType<typeof useGetCurrentDoc>['document'];

interface Props extends Pick<
  OutlineMenuViewProps,
  'documentName' | 'handleScrollTo' | 'headings' | 'selectedHeadingIndex' | 'areHeadingsSupported'
> {
  document: DocumentType;
  isOpen: boolean;
  dataCy?: string;
  isLoading?: boolean;
  isSelected?: boolean;
  onClick?(): void;
  onDelete?(): void;
}

export const DocumentHeadings: React.FC<Props> = ({
  areHeadingsSupported,
  dataCy,
  document,
  documentName,
  handleScrollTo,
  headings,
  isLoading,
  isOpen,
  isSelected,
  onClick,
  onDelete,
  selectedHeadingIndex,
}) => {
  const { classes } = useStyles();

  if (!document) return null;

  return (
    <div data-cy={dataCy}>
      <div
        className={clsx('document-header', classes.header, {
          [classes.isOpen]: isOpen,
          [classes.loaded]: isSelected && !isLoading,
          [classes.loading]: isSelected && isLoading,
          [classes.interactive]: !!onClick,
        })}
      >
        <button className={classes.headerBtn} disabled={!onClick} type="button" onClick={onClick}>
          <StorableIcon
            iconProps={{
              height: 16,
              width: 16,
            }}
            storable={document}
          />
          <Typography className={classes.documentName} title={documentName}>
            {documentName}
          </Typography>
        </button>
        {onDelete && (
          <IconButton
            aria-label="Delete document"
            className={classes.deleteBtn}
            size="small"
            sx={{ height: '26px', width: '26px' }}
            onClick={onDelete}
          >
            <TrashcanIcon sx={{ height: '16px', width: '16px' }} />
          </IconButton>
        )}
      </div>
      {areHeadingsSupported && (
        <Collapse in={isOpen && !!headings} orientation="vertical">
          <HeadingsList
            handleScrollTo={handleScrollTo}
            headings={headings}
            selectedHeadingIndex={selectedHeadingIndex}
          />
        </Collapse>
      )}
    </div>
  );
};
